import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Chip,
  Slide,
} from "@mui/material";
import {
  Call,
  CallEnd,
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  ScreenShare,
  StopScreenShare,
  Fullscreen,
  FullscreenExit,
  PhoneInTalk,
} from "@mui/icons-material";
import { useCall } from "../../contexts/CallContext";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function CallOverlay() {
  const {
    callState,
    callType,
    targetUser,
    incomingCallData,
    localStream,
    remoteStream,
    isMuted,
    isVideoOff,
    isScreenSharing,
    callDuration,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    toggleScreenShare,
  } = useCall();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Attach local media stream
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, callState]);

  // Attach remote media stream
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream, callState]);

  const formatDuration = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  // If idle, don't render anything
  if (callState === "idle") return null;

  // 1. INCOMING CALL DIALOG
  if (callState === "incoming") {
    return (
      <Dialog
        open={true}
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            background: "linear-gradient(145deg, #1e293b, #0f172a)",
            color: "#ffffff",
            p: 3,
            minWidth: "340px",
            textAlign: "center",
            boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
            border: "1px solid rgba(255,255,255,0.1)",
          },
        }}
      >
        <DialogContent sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
          {/* Animated Pulsing Ring */}
          <Box
            sx={{
              position: "relative",
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&::before": {
                content: '""',
                position: "absolute",
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                background: "rgba(0, 167, 142, 0.4)",
                animation: "pulseRing 1.5s infinite ease-out",
              },
              "@keyframes pulseRing": {
                "0%": { transform: "scale(0.9)", opacity: 1 },
                "100%": { transform: "scale(1.4)", opacity: 0 },
              },
            }}
          >
            <Avatar
              src={incomingCallData?.callerAvatar}
              sx={{
                width: 90,
                height: 90,
                border: "4px solid #00a78e",
                boxShadow: "0 8px 24px rgba(0, 167, 142, 0.5)",
              }}
            >
              {incomingCallData?.callerName?.[0]?.toUpperCase() || "U"}
            </Avatar>
          </Box>

          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            {incomingCallData?.callerName || "Unknown Caller"}
          </Typography>

          <Chip
            icon={incomingCallData?.isVideo ? <Videocam sx={{ color: "#fff !important" }} /> : <PhoneInTalk sx={{ color: "#fff !important" }} />}
            label={incomingCallData?.isVideo ? "Incoming Video Call..." : "Incoming Voice Call..."}
            sx={{
              background: "rgba(255,255,255,0.1)",
              color: "#e2e8f0",
              fontWeight: 600,
              mb: 4,
            }}
          />

          {/* Action Buttons */}
          <Box display="flex" gap={4} justifyContent="center" alignItems="center">
            {/* Decline */}
            <Tooltip title="Decline">
              <IconButton
                onClick={() => rejectCall("declined")}
                sx={{
                  background: "linear-gradient(135deg, #ef4444, #dc2626)",
                  color: "#fff",
                  width: 64,
                  height: 64,
                  boxShadow: "0 8px 20px rgba(239, 68, 68, 0.4)",
                  "&:hover": { transform: "scale(1.1)", background: "#dc2626" },
                  transition: "all 0.2s",
                }}
              >
                <CallEnd sx={{ fontSize: 32 }} />
              </IconButton>
            </Tooltip>

            {/* Accept */}
            <Tooltip title="Accept Call">
              <IconButton
                onClick={acceptCall}
                sx={{
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "#fff",
                  width: 64,
                  height: 64,
                  boxShadow: "0 8px 20px rgba(16, 185, 129, 0.4)",
                  "&:hover": { transform: "scale(1.1)", background: "#059669" },
                  transition: "all 0.2s",
                }}
              >
                <Call sx={{ fontSize: 32 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // 2. OUTGOING CALL DIALOG
  if (callState === "outgoing") {
    return (
      <Dialog
        open={true}
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            background: "linear-gradient(145deg, #1e293b, #0f172a)",
            color: "#ffffff",
            p: 3,
            minWidth: "340px",
            textAlign: "center",
            boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
            border: "1px solid rgba(255,255,255,0.1)",
          },
        }}
      >
        <DialogContent sx={{ p: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Box
            sx={{
              position: "relative",
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&::before": {
                content: '""',
                position: "absolute",
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                background: "rgba(59, 130, 246, 0.4)",
                animation: "pulseRingBlue 1.5s infinite ease-out",
              },
              "@keyframes pulseRingBlue": {
                "0%": { transform: "scale(0.9)", opacity: 1 },
                "100%": { transform: "scale(1.4)", opacity: 0 },
              },
            }}
          >
            <Avatar
              src={targetUser?.avatar}
              sx={{
                width: 90,
                height: 90,
                border: "4px solid #3b82f6",
                boxShadow: "0 8px 24px rgba(59, 130, 246, 0.5)",
              }}
            >
              {targetUser?.firstName?.[0] || targetUser?.name?.[0] || "U"}
            </Avatar>
          </Box>

          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            {targetUser?.firstName ? `${targetUser.firstName} ${targetUser.lastName || ""}` : targetUser?.name || "Calling..."}
          </Typography>

          <Typography variant="body2" sx={{ color: "#94a3b8", mb: 4 }}>
            Calling ({callType === "video" ? "Video" : "Voice"})...
          </Typography>

          {/* End/Cancel Button */}
          <Tooltip title="Cancel Call">
            <IconButton
              onClick={endCall}
              sx={{
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
                color: "#fff",
                width: 64,
                height: 64,
                boxShadow: "0 8px 20px rgba(239, 68, 68, 0.4)",
                "&:hover": { transform: "scale(1.1)", background: "#dc2626" },
                transition: "all 0.2s",
              }}
            >
              <CallEnd sx={{ fontSize: 32 }} />
            </IconButton>
          </Tooltip>
        </DialogContent>
      </Dialog>
    );
  }

  // 3. CONNECTED LIVE CALL WINDOW
  return (
    <Box
      sx={{
        position: "fixed",
        top: isFullscreen ? 0 : "auto",
        bottom: isFullscreen ? 0 : 24,
        right: isFullscreen ? 0 : 24,
        left: isFullscreen ? 0 : "auto",
        width: isFullscreen ? "100vw" : { xs: "95vw", sm: "640px" },
        height: isFullscreen ? "100vh" : { xs: "75vh", sm: "460px" },
        zIndex: 99999,
        borderRadius: isFullscreen ? 0 : "20px",
        overflow: "hidden",
        boxShadow: "0 25px 60px rgba(0,0,0,0.8)",
        background: "#090d16",
        border: isFullscreen ? "none" : "2px solid rgba(0, 167, 142, 0.4)",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s ease",
      }}
    >
      {/* Top Floating Header */}
      <Box
        sx={{
          position: "absolute",
          top: 16,
          left: 16,
          right: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 10,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar src={targetUser?.avatar} sx={{ width: 36, height: 36 }}>
            {targetUser?.firstName?.[0] || targetUser?.name?.[0] || "U"}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" color="#fff" fontWeight={600}>
              {targetUser?.firstName ? `${targetUser.firstName} ${targetUser.lastName || ""}` : targetUser?.name || "In Call"}
            </Typography>
            <Chip
              label={formatDuration(callDuration)}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.75rem",
                background: "rgba(0,0,0,0.5)",
                color: "#10b981",
                fontWeight: 700,
                border: "1px solid rgba(16,185,129,0.3)",
              }}
            />
          </Box>
        </Box>

        <IconButton
          onClick={toggleFullscreen}
          sx={{
            color: "#fff",
            background: "rgba(0,0,0,0.4)",
            backdropFilter: "blur(10px)",
            "&:hover": { background: "rgba(0,0,0,0.7)" },
          }}
        >
          {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
        </IconButton>
      </Box>

      {/* Main Video View (Remote User) */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          height: "100%",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#0b1329",
        }}
      >
        {callType === "video" ? (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          /* Voice Call Visualizer */
          <Box textAlign="center" py={6}>
            <Avatar
              src={targetUser?.avatar}
              sx={{
                width: 120,
                height: 120,
                margin: "0 auto 20px",
                border: "4px solid #00a78e",
                boxShadow: "0 0 35px rgba(0, 167, 142, 0.6)",
              }}
            >
              {targetUser?.firstName?.[0] || targetUser?.name?.[0] || "U"}
            </Avatar>
            <Typography variant="h5" color="#fff" fontWeight={700}>
              {targetUser?.firstName ? `${targetUser.firstName} ${targetUser.lastName || ""}` : targetUser?.name || "User"}
            </Typography>
            <Typography variant="body2" color="#00a78e" fontWeight={600} mt={1}>
              ● Voice Call Active
            </Typography>
          </Box>
        )}

        {/* Local Video (Picture-in-Picture in Corner) */}
        {callType === "video" && (
          <Paper
            elevation={8}
            sx={{
              position: "absolute",
              top: 64,
              right: 16,
              width: { xs: 100, sm: 140 },
              height: { xs: 70, sm: 100 },
              borderRadius: "12px",
              overflow: "hidden",
              border: "2px solid rgba(255,255,255,0.4)",
              background: "#1e293b",
              zIndex: 10,
            }}
          >
            {isVideoOff ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
                height="100%"
                bgcolor="#1e293b"
                color="#94a3b8"
              >
                <VideocamOff fontSize="small" />
              </Box>
            ) : (
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transform: "scaleX(-1)", // Mirror selfie preview
                }}
              />
            )}
          </Paper>
        )}
      </Box>

      {/* Floating Bottom Control Bar */}
      <Box
        sx={{
          position: "absolute",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 1.5,
          px: 3,
          background: "rgba(15, 23, 42, 0.75)",
          backdropFilter: "blur(16px)",
          borderRadius: "32px",
          border: "1px solid rgba(255,255,255,0.15)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
          zIndex: 20,
        }}
      >
        {/* Toggle Mic */}
        <Tooltip title={isMuted ? "Unmute Mic" : "Mute Mic"}>
          <IconButton
            onClick={toggleMute}
            sx={{
              background: isMuted ? "rgba(239, 68, 68, 0.3)" : "rgba(255,255,255,0.15)",
              color: isMuted ? "#ef4444" : "#ffffff",
              "&:hover": { background: isMuted ? "rgba(239, 68, 68, 0.5)" : "rgba(255,255,255,0.3)" },
            }}
          >
            {isMuted ? <MicOff /> : <Mic />}
          </IconButton>
        </Tooltip>

        {/* Toggle Camera (If Video Call) */}
        {callType === "video" && (
          <Tooltip title={isVideoOff ? "Turn Video On" : "Turn Video Off"}>
            <IconButton
              onClick={toggleVideo}
              sx={{
                background: isVideoOff ? "rgba(239, 68, 68, 0.3)" : "rgba(255,255,255,0.15)",
                color: isVideoOff ? "#ef4444" : "#ffffff",
                "&:hover": { background: isVideoOff ? "rgba(239, 68, 68, 0.5)" : "rgba(255,255,255,0.3)" },
              }}
            >
              {isVideoOff ? <VideocamOff /> : <Videocam />}
            </IconButton>
          </Tooltip>
        )}

        {/* Screen Share */}
        <Tooltip title={isScreenSharing ? "Stop Screen Share" : "Share Screen"}>
          <IconButton
            onClick={toggleScreenShare}
            sx={{
              background: isScreenSharing ? "rgba(16, 185, 129, 0.4)" : "rgba(255,255,255,0.15)",
              color: isScreenSharing ? "#10b981" : "#ffffff",
              "&:hover": { background: isScreenSharing ? "rgba(16, 185, 129, 0.6)" : "rgba(255,255,255,0.3)" },
            }}
          >
            {isScreenSharing ? <StopScreenShare /> : <ScreenShare />}
          </IconButton>
        </Tooltip>

        {/* End Call Button */}
        <Tooltip title="End Call">
          <IconButton
            onClick={endCall}
            sx={{
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              color: "#ffffff",
              width: 48,
              height: 48,
              boxShadow: "0 4px 16px rgba(239, 68, 68, 0.5)",
              "&:hover": { transform: "scale(1.1)", background: "#dc2626" },
              transition: "all 0.2s",
            }}
          >
            <CallEnd />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
